import { AnalyticsEvent, useAnalytics } from '@aninix/analytics'
import { createProjectV2, syncProjectWithRemote } from '@aninix/api'
import { Loader } from '@aninix/app-design-system'
import { featureFlags } from '@aninix/core'
import { config } from '@aninix/core/config'
import { svgToAni } from '@aninix/core/use-cases'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from '../../../modules/toasts'

export const CreateProjectFromSvgButton: React.FCC = () => {
  const [isCreating, setIsCreating] = React.useState<boolean>(false)
  const inputFileRef = React.useRef<HTMLInputElement>(null)
  const { teamId } = useParams()
  const analytics = useAnalytics()

  const trackProjectEvent = React.useCallback(
    (eventName: AnalyticsEvent, properties: Record<string, any> = {}) => {
      analytics.track({
        eventName,
        properties: {
          space: teamId ? 'team' : 'personal',
          from: 'svg',
          ...properties,
        },
      })
    },
    [analytics, teamId]
  )

  const createProject = React.useCallback(
    async (data: string, from: 'clipboard' | 'file', name?: string) => {
      setIsCreating(true)
      trackProjectEvent(AnalyticsEvent.ProjectCreateClick, { method: from })

      const start = data.indexOf('<svg')
      const end = data.indexOf('</svg>') + 6
      const svg = data.slice(start, end)

      try {
        const project = svgToAni(svg)
        await createProjectV2({
          id: project.id,
          projectName: name ?? 'New project',
          teamId,
        })
        await syncProjectWithRemote(project)
        window.open(`${config.webAppUrl}/edit/${project.id}`, '_blank')
        trackProjectEvent(AnalyticsEvent.ProjectCreated, {
          method: from,
          context: 'webapp.dashboard',
        })
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Unknown error'
        toast('Failed to create project', { variant: 'error' })
        trackProjectEvent(AnalyticsEvent.ProjectCreationFailed, {
          method: from,
          error: errorMessage,
        })
      } finally {
        setIsCreating(false)
        if (inputFileRef.current) inputFileRef.current.value = ''
      }
    },
    [trackProjectEvent, teamId]
  )

  const readClipboard = React.useCallback(async () => {
    const data = await navigator.clipboard.readText()
    const isSvg = data?.includes('<svg')

    if (isSvg) {
      createProject(data, 'clipboard')
    } else {
      inputFileRef.current?.dispatchEvent(new MouseEvent('click'))
    }
  }, [createProject])

  const readFile = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.currentTarget.files?.[0]
      if (!file) return
      if (file.type !== 'image/svg+xml') {
        toast('Please select a .svg file', { variant: 'error' })
        trackProjectEvent(AnalyticsEvent.ProjectCreationFailed, {
          method: 'file',
          error: 'Invalid file type',
        })
        return
      }

      const reader = new FileReader()

      reader.onload = (e) => {
        const svg = e.target?.result
        if (typeof svg !== 'string') {
          toast('Failed to read file', { variant: 'error' })
          trackProjectEvent(AnalyticsEvent.ProjectCreationFailed, {
            method: 'file',
            error: 'Failed to read file',
          })
          return
        }

        createProject(svg, 'file', file.name.slice(0, -4))
      }
      reader.onerror = () => {
        toast('Failed to read file', { variant: 'error' })
        trackProjectEvent(AnalyticsEvent.ProjectCreationFailed, {
          method: 'file',
          error: 'File read error',
        })
      }
      reader.readAsText(file)
    },
    [createProject, trackProjectEvent]
  )

  if (!featureFlags.importSvg) return null

  return (
    <button
      className="flex flex-row w-full justify-between items-center gap-2 bg-secondary px-3 py-2 font-body text-sm font-medium text-white transition-all duration-300 hover:shadow-lg relative"
      type="button"
      onClick={readClipboard}
      disabled={isCreating}
    >
      <p>From .svg</p>
      {isCreating && <Loader size={16} color="#fff" />}
      <input
        type="file"
        ref={inputFileRef}
        onChange={readFile}
        title={''}
        className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-0"
        accept=".svg"
        disabled={isCreating}
      />
    </button>
  )
}
